import {
  buildGatherCamerasRequestBodyWithVpnTunnel,
  channelRowsFromGatherResponse,
  formatDashcamOutcomeMessage,
  isDashcamOutcomeFailure,
  isDashcamRequestCancelled,
  mapDashcamHttpError,
  postCameras,
} from "../../../../services/capabilitiesService";
import deriveRtspUrlSuffixTemplate from "./deriveRtspUrlSuffixTemplate";
import installationToVideoRecorderShape from "./installationToVideoRecorderShape";

export function cloudDeviceHost(installation) {
  if (!installation?.is_cloud) return null;
  return installation.ddns_url || installation.wan_ipv4 || null;
}

export function dashcamProbeBlocker(installation, dvr) {
  if (!dvr?.dvr_login) return "Missing DVR login.";
  if (!dvr?.dvr_password) return "Missing DVR password.";
  if (installation?.is_cloud) {
    if (!cloudDeviceHost(installation)) return "Missing DDNS hostname / WAN IP.";
    if (!dvr?.dvr_external_http_port) return "Missing DVR external HTTP port.";
  } else {
    if (!installation?.server_name) return "Installation has no server assigned.";
    if (!dvr?.dvr_ipv4) return "Missing DVR IPv4.";
    if (!dvr?.dvr_http_port) return "Missing DVR HTTP port.";
  }
  return null;
}

// Cache key for stashed probe results; discard the result when the fingerprint stops matching.
export function installationProbeFingerprint(installation, dvr) {
  if (!installation || !dvr) return "";
  return JSON.stringify([
    dvr.id ?? dvr.tempId ?? null,
    installation.is_cloud ?? null,
    installation.server_name ?? "",
    installation.wan_ipv4 ?? "",
    installation.ddns_url ?? "",
    dvr.dvr_ipv4 ?? "",
    dvr.dvr_login ?? "",
    dvr.dvr_password ?? "",
    dvr.dvr_http_port ?? "",
    dvr.dvr_external_http_port ?? "",
    dvr.dvr_rtsp_port ?? "",
    dvr.dvr_external_rtsp_port ?? "",
    dvr.rtsp_url_suffix ?? "",
  ]);
}

// Cloud installs talk to Dashcam directly via DDNS/WAN; local installs route through
// the inference machine's VPN tunnel.
export async function probeInstallationViaDashcam({ installation, dvr, inferenceHostname, signal } = {}) {
  const blocker = dashcamProbeBlocker(installation, dvr);
  if (blocker) throw new Error(blocker);
  const isCloud = Boolean(installation?.is_cloud);
  const videoRecorder = installationToVideoRecorderShape({
    dvr,
    inferenceHostname: isCloud ? null : inferenceHostname,
    deviceHost: isCloud ? cloudDeviceHost(installation) : null,
  });
  let response;
  try {
    const payload = await buildGatherCamerasRequestBodyWithVpnTunnel(
      videoRecorder,
      installation?.id ?? 0,
      { localInference: !isCloud },
      { signal }
    );
    response = await postCameras(payload, { signal });
  } catch (err) {
    if (isDashcamRequestCancelled(err)) throw err;
    const error = new Error(mapDashcamHttpError(err));
    error.cause = err;
    throw error;
  }
  const data = response?.data;
  if (isDashcamOutcomeFailure(data)) {
    throw new Error(formatDashcamOutcomeMessage(data) ?? "Dashcam reported a failure.");
  }
  const channels = channelRowsFromGatherResponse(data);
  const suffixTemplate = deriveRtspUrlSuffixTemplate(channels);
  return { channels, raw: data, suffixTemplate };
}
